import { AlertTriangle, Info, RotateCcw, ShieldAlert, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { AlertaSpec } from '../asistente.types';

interface AlertListProps {
  alertas: AlertaSpec[];
  /** Cuántos avisos ha cerrado el usuario (se pueden volver a mostrar). */
  descartadas?: number;
  onSelect: (pregunta: string) => void;
  onDescartar?: (tipo: string) => void;
  onRestaurar?: () => void;
  disabled?: boolean;
}

/** Icono según la gravedad del aviso. */
function iconoDe(nivel: string) {
  if (nivel === 'danger') return ShieldAlert;
  if (nivel === 'warning') return AlertTriangle;

  return Info;
}

/**
 * Avisos detectados en los datos, con un botón para pedir el análisis.
 *
 * El asistente no se ejecuta solo: calcula los avisos con consultas de lectura
 * y aquí se ofrecen como preguntas, para que lo que requiere atención aparezca
 * sin que el usuario tenga que saber que existe.
 *
 * Cada aviso se puede **cerrar**: se recuerda por usuario y deja de mostrarse
 * unos días (si el problema sigue, vuelve a avisar). El pie permite recuperar
 * los cerrados, para que no se pierdan por un descuido.
 */
export function AlertList({
  alertas,
  descartadas = 0,
  onSelect,
  onDescartar,
  onRestaurar,
  disabled = false,
}: AlertListProps) {
  if (alertas.length === 0 && descartadas === 0) return null;

  return (
    <section aria-label="Avisos detectados" className="space-y-2">
      {alertas.map((alerta) => {
        const Icono = iconoDe(alerta.nivel);

        return (
          <div
            key={alerta.tipo}
            className={cn(
              'flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border px-3 py-2',
              alerta.nivel === 'danger'
                ? 'border-rose-200 bg-rose-50'
                : alerta.nivel === 'warning'
                  ? 'border-amber-200 bg-amber-50'
                  : 'border-ink-100 bg-ink-50',
            )}
          >
            <Icono
              className={cn(
                'size-4 shrink-0',
                alerta.nivel === 'danger'
                  ? 'text-rose-600'
                  : alerta.nivel === 'warning'
                    ? 'text-amber-600'
                    : 'text-ink-500',
              )}
              aria-hidden="true"
            />

            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-ink-900">{alerta.titulo}</p>
              <p className="text-[11px] leading-snug text-ink-600">{alerta.detalle}</p>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => onSelect(alerta.pregunta)}
                disabled={disabled}
                className={cn(
                  'cursor-pointer rounded-lg border border-ink-200 bg-white px-2.5 py-1',
                  'text-[11px] font-medium text-ink-700 transition-colors hover:border-brand-200 hover:text-brand-600',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                )}
              >
                Analizar
              </button>

              {onDescartar ? (
                <button
                  type="button"
                  onClick={() => onDescartar(alerta.tipo)}
                  aria-label={`Cerrar el aviso: ${alerta.titulo}`}
                  title="Cerrar este aviso (dejará de mostrarse unos días)"
                  className="cursor-pointer rounded-md p-1 text-ink-400 transition-colors hover:bg-white hover:text-ink-700"
                >
                  <X className="size-3.5" />
                </button>
              ) : null}
            </div>
          </div>
        );
      })}

      {descartadas > 0 && onRestaurar ? (
        <button
          type="button"
          onClick={onRestaurar}
          className={cn(
            'inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1',
            'text-[11px] font-medium text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-700',
          )}
        >
          <RotateCcw className="size-3" aria-hidden="true" />
          {descartadas} aviso(s) cerrado(s) — volver a mostrarlos
        </button>
      ) : null}
    </section>
  );
}
